/**
 * 저장 시도별 manifest save plan 보관소 (STUDIO-PACKAGE-003).
 *
 * manifest save는 두 단계에 걸쳐 있다. 먼저 Unity에 "이 Space만 담아라"고 요청하고,
 * Unity가 partial VDB를 만든 뒤에야 Main API에 `space_manifest`를 제출한다. 그 사이를
 * 잇는 것이 `saveAttemptId`다.
 *
 * plan을 만든 hook과 제출하는 runtime은 여러 계층 떨어져 있어 직접 전달할 수 없으므로,
 * 저장소가 이미 쓰는 module-level runtime 방식으로 시도 단위로 보관한다.
 *
 * 정확성 규칙:
 *  - plan은 **한 번만** 소비된다. 다음 저장이 앞 시도의 plan을 재사용하면 잘못된 Space를
 *    삭제할 수 있다.
 *  - Unity가 돌려준 `partial_space_indices`가 요청과 정확히 같을 때만 사용한다. 다르면
 *    Unity가 다른 내용을 담았다는 뜻이므로 manifest를 버리고 legacy full save로 떨어진다.
 */

import {
  buildSpaceManifest,
  decideManifestSaveRouting,
  isManifestSaveEnabled,
  type SavedSpaceOrigin,
  type SpaceManifestPlan,
} from "./saveSpaceManifest";

/** 동시에 진행 가능한 저장 시도는 소수다. 누수를 막기 위한 상한. */
const MAX_STAGED_PLANS = 8;

const stagedPlans = new Map<string, SpaceManifestPlan>();

function normalizeAttemptId(saveAttemptId: string | undefined | null): string {
  return (saveAttemptId ?? "").trim();
}

export function stageManifestSavePlan(
  saveAttemptId: string | undefined | null,
  plan: SpaceManifestPlan,
): boolean {
  const attemptId = normalizeAttemptId(saveAttemptId);
  if (!attemptId) return false;

  // 오래된 시도부터 버려 map이 무한히 자라지 않게 한다.
  while (stagedPlans.size >= MAX_STAGED_PLANS) {
    const oldest = stagedPlans.keys().next();
    if (oldest.done) break;
    stagedPlans.delete(oldest.value);
  }

  stagedPlans.set(attemptId, {
    manifest: plan.manifest.map((entry) => ({ ...entry })),
    vdbSpaceIndices: [...plan.vdbSpaceIndices],
  });
  return true;
}

export function peekManifestSavePlan(
  saveAttemptId: string | undefined | null,
): SpaceManifestPlan | null {
  const attemptId = normalizeAttemptId(saveAttemptId);
  if (!attemptId) return null;
  return stagedPlans.get(attemptId) ?? null;
}

export function clearManifestSavePlan(
  saveAttemptId: string | undefined | null,
): void {
  const attemptId = normalizeAttemptId(saveAttemptId);
  if (!attemptId) return;
  stagedPlans.delete(attemptId);
}

function sameIndices(
  expected: readonly number[],
  actual: readonly number[],
): boolean {
  if (expected.length !== actual.length) return false;
  return expected.every((value, index) => value === actual[index]);
}

export interface ConsumeManifestSavePlanResult {
  plan: SpaceManifestPlan | null;
  reason:
    | "manifest_save_selected"
    | "legacy_full_save"
    | "no_staged_plan"
    | "unity_partial_space_mismatch";
  /**
   * Unity가 partial VDB를 만들었는지.
   *
   * `true`인데 `plan`이 없으면 **legacy full save로 물러설 수 없다.** partial VDB는
   * 일부 Space만 담고 있어서, 전체 snapshot으로 해석하는 legacy 경로로 보내면 나머지
   * Space를 모두 지운다. 이 조합은 저장 실패로 종결해야 한다.
   */
  unityPackedPartialVdb: boolean;
}

/**
 * 제출 직전에 plan을 꺼낸다. 성공·실패 어느 쪽이든 보관분은 제거한다.
 *
 * `unityPartialSpaceIndices`는 Unity `saveJsonComplete`가 돌려준 `partial_space_indices`다.
 * 값이 없으면 Unity가 legacy full save로 처리했다는 뜻이므로 manifest를 쓰지 않는다.
 */
export function consumeManifestSavePlan(
  saveAttemptId: string | undefined | null,
  unityPartialSpaceIndices: readonly number[] | null | undefined,
): ConsumeManifestSavePlanResult {
  const attemptId = normalizeAttemptId(saveAttemptId);
  const plan = attemptId ? stagedPlans.get(attemptId) : undefined;
  if (attemptId) stagedPlans.delete(attemptId);

  // VDB의 실제 내용이 endpoint를 결정한다. React의 장부가 아니다.
  const unityPackedPartialVdb = Array.isArray(unityPartialSpaceIndices);

  if (!unityPackedPartialVdb) {
    // Unity가 전체 snapshot을 담았다. legacy full save가 유일하게 맞는 경로다.
    return { plan: null, reason: "legacy_full_save", unityPackedPartialVdb: false };
  }

  if (!plan) {
    return { plan: null, reason: "no_staged_plan", unityPackedPartialVdb: true };
  }
  if (!sameIndices(plan.vdbSpaceIndices, unityPartialSpaceIndices!)) {
    // Unity가 요청과 다른 Space를 담았다. 이 manifest는 그 VDB를 설명하지 못한다.
    return {
      plan: null,
      reason: "unity_partial_space_mismatch",
      unityPackedPartialVdb: true,
    };
  }
  return { plan, reason: "manifest_save_selected", unityPackedPartialVdb: true };
}

/** 테스트 전용: 보관소를 비운다. */
export function resetManifestSavePlansForTest(): void {
  stagedPlans.clear();
}

/**
 * 저장 시작 시점의 Space 상태 공급자.
 *
 * 원본 매핑과 dirty 목록은 editor hook이 들고 있고, 저장 시작 지점은 여러 곳이다.
 * 각 시작 지점을 고치는 대신 bridge port 한 곳에서 이 공급자를 통해 plan을 만든다.
 */
export interface ManifestPlanSnapshot {
  origins: readonly SavedSpaceOrigin[];
  dirtySpaceIndices: readonly number[];
  savedSpaceCount: number;
  currentSpaceCount: number;
  hasBaseline: boolean;
}

export type ManifestPlanProvider = () => ManifestPlanSnapshot | null;

let manifestPlanProvider: ManifestPlanProvider | null = null;

/** editor hook이 mount 동안 등록한다. 반환된 함수로 해제한다. */
export function registerManifestPlanProvider(
  provider: ManifestPlanProvider,
): () => void {
  manifestPlanProvider = provider;
  return () => {
    if (manifestPlanProvider === provider) manifestPlanProvider = null;
  };
}

/**
 * 저장 시작 시 manifest save 여부를 정하고, 쓰기로 했으면 plan을 보관한다.
 *
 * 반환값은 Unity에 보낼 partial VDB 대상 Space index다. `null`이면 legacy full save다.
 * flag가 꺼져 있거나 공급자가 없거나 routing이 거부하면 항상 `null`이다.
 */
export function buildAndStageManifestPlan(
  saveAttemptId: string | undefined | null,
): number[] | null {
  if (!isManifestSaveEnabled()) return null;
  if (!normalizeAttemptId(saveAttemptId)) return null;

  const snapshot = manifestPlanProvider?.() ?? null;
  if (!snapshot) return null;

  const plan = buildSpaceManifest({
    origins: snapshot.origins,
    dirtySpaceIndices: snapshot.dirtySpaceIndices,
  });
  const decision = decideManifestSaveRouting({
    enabled: true,
    hasBaseline: snapshot.hasBaseline,
    savedSpaceCount: snapshot.savedSpaceCount,
    currentSpaceCount: snapshot.currentSpaceCount,
    plan,
  });
  if (!decision.useManifestSave) return null;
  // routing이 이미 막지만, Space 0개 VDB는 어떤 경로로도 만들지 않는다.
  if (plan.vdbSpaceIndices.length === 0) return null;

  if (!stageManifestSavePlan(saveAttemptId, plan)) return null;
  return [...plan.vdbSpaceIndices];
}

/** 테스트 전용: 등록된 공급자를 제거한다. */
export function resetManifestPlanProviderForTest(): void {
  manifestPlanProvider = null;
}
